import React from "react";
import LegalLayout from "@/components/LegalLayout";
import { site } from "@/config/site";

export default function Terms() {
  return (
    <LegalLayout title="Terms &amp; Conditions" updatedOn="April 2026">
      <p className="italic text-navy/60">
        This document is a draft. It has not been reviewed by a lawyer. Please
        have it reviewed before going live, especially clause 8 on limitation of
        liability.
      </p>

      <h2>1. Acceptance</h2>
      <p>
        By using the VaultSafe service you agree to these terms. If you do not
        agree, please do not use the service.
      </p>

      <h2>2. The service</h2>
      <p>
        VaultSafe is a managed cloud backup service that copies files and folders
        from your computers to secure cloud storage and restores them on request.{" "}
        <strong>
          VaultSafe is not an image or bare-metal recovery service. It does not
          back up your operating system or installed programs.
        </strong>
      </p>

      <h2>2A. Where your data is stored</h2>
      <p>
        Your backup data is stored on <strong>encrypted, enterprise-grade
        cloud infrastructure across multiple secure data centres</strong>.
        Copies of your data are held in more than one geographic location for
        redundancy and disaster recovery. The data centres we use hold
        internationally recognised information security certifications,
        including <strong>ISO/IEC 27001</strong>. Your data remains encrypted
        in transit and at rest, and access controls apply as described in our
        Privacy Policy. Specific storage-location details for your account can
        be disclosed on written request for compliance or audit purposes.
      </p>

      <h2>3. Your responsibilities</h2>
      <ul>
        <li>Tell us which folders matter, and keep that list current.</li>
        <li>
          Keep the backup agent installed and the computer powered on and
          connected often enough for backups to run.
        </li>
        <li>Do not use the service to back up illegal content.</li>
        <li>Keep your account credentials secure.</li>
      </ul>

      <h2>4. Storage limits</h2>
      <p>
        Each plan has a storage cap. When you approach the cap we will warn you
        at 80% and 95% of your allocation. If you exceed the cap, new backups
        will pause until you upgrade or free up space.
      </p>

      <h2>5. Retention</h2>
      <p>
        File history is limited to your plan's retention period. Older versions
        of files are deleted automatically after that period.
      </p>

      <h2>6. Restores</h2>
      <p>
        Restore requests are handled during business hours ({site.businessHours}).
        We aim for same-day restore for common requests, but we do not guarantee
        instant recovery — larger restores or restores of older backups may take
        longer.
      </p>

      <h2>7. Availability</h2>
      <p>
        We aim for high availability of the service. We do not guarantee
        uninterrupted service. Planned maintenance will be notified in advance
        where possible.
      </p>

      <h2>8. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, VaultSafe's total liability to
        you for any claim arising out of the service is capped at the fees you
        have paid to us in the twelve months preceding the claim. VaultSafe is a
        backup service, not an insurance product against business loss.
      </p>
      <p className="italic text-navy/60">
        This clause in particular should be reviewed by your lawyer.
      </p>

      <h2>9. What we are not liable for</h2>
      <ul>
        <li>
          Data that was never included in the backup selection you gave us.
        </li>
        <li>
          Data lost because the computer was off, offline, or the agent was
          uninstalled or blocked by antivirus.
        </li>
        <li>Damage caused before the backup was set up.</li>
      </ul>

      <h2>10. Payment terms</h2>
      <p>
        Fees are billed annually in advance. GST is charged extra as applicable.
        Non-payment triggers the following steps, with notice at each stage: a
        grace period, then suspension of new backups, then permanent deletion of
        stored data after a further period.
      </p>

      <h2>11. Suspension and termination</h2>
      <p>
        You may cancel your subscription at any time to prevent renewal (see our
        Refund &amp; Cancellation Policy). We may suspend or terminate service
        for material breach of these terms or for non-payment.
      </p>

      <h2>12. Your data on termination</h2>
      <p>
        After cancellation, we hold your backup data for 30 days so you can
        export it or reactivate. After 30 days it is permanently deleted.
      </p>

      <h2>13. Confidentiality</h2>
      <p>
        We treat your business data as confidential and will not disclose it
        except as required to deliver the service or by law.
      </p>

      <h2>14. Changes to terms and pricing</h2>
      <p>
        We may update these terms and our pricing. Existing annual subscribers
        will not see price changes mid-cycle. Changes will be notified by email
        and on our website.
      </p>

      <h2>15. Governing law and jurisdiction</h2>
      <p>
        These terms are governed by the laws of India. The courts of Mumbai,
        Maharashtra have exclusive jurisdiction.
      </p>

      <h2>16. Contact</h2>
      <p>
        Questions? Write to <a href={`mailto:${site.email}`}>{site.email}</a> or
        post to {site.legalEntity}, {site.address.line1}, {site.address.line2}.
      </p>
    </LegalLayout>
  );
}
